import * as THREE from 'three';

import { GameLoop } from './GameLoop';
import { DebugControls } from '../controls/DebugControls';
import { createInputState, type InputState } from '../controls/InputState';
import { InputRouter } from '../controls/InputRouter';
import { GyroController } from '../controls/GyroController';
import { MobileControls } from '../controls/MobileControls';
import { GameAssets, GAME_MODEL_PATHS, type AssetReport } from '../assets/GameAssets';
import { Ball } from '../game/Ball';
import { MazeAssembly } from '../game/MazeAssembly';
import { ScoreRowRig } from '../game/ScoreRowRig';
import { COUNTDOWN, BALL, ROTATION, ROUND, GAME } from '../game/constants';
import { GameState } from '../game/GameState';
import { buildMazeLayout, type MazeLayout } from '../game/mazeLayout';
import { SCORE_ROW, resolveSlot, slotsLeftToRight } from '../game/scoreRow';
import { ScoringTracker, type ScoreResult } from '../game/scoring';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { CameraRig } from '../scene/CameraRig';
import { buildLighting } from '../scene/Lighting';
import { SceneManager } from '../scene/SceneManager';
import { Display } from '../ui/Display';
import { DebugView } from '../ui/DebugView';
import { Hud } from '../ui/Hud';
import { FlowOverlay } from '../ui/FlowOverlay';
import { stateLabel } from '../ui/strings.zh-HK';
import {
  installDebugApi,
  type BoundsSnapshot,
  type PixelSample,
  type RadialExtentSnapshot,
} from '../utils/debug';
import { loadBest, saveBest } from '../utils/bestScore';
import { clamp, degToRad, moveTowards } from '../utils/math';

/** Flatten a `Box3` into plain numbers, so the debug API stays serialisable. */
function toBounds(box: THREE.Box3): BoundsSnapshot {
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  return {
    min: [box.min.x, box.min.y, box.min.z],
    max: [box.max.x, box.max.y, box.max.z],
    center: [center.x, center.y, center.z],
    size: [size.x, size.y, size.z],
  };
}

/** Depth-first search of a subtree for a named node. */
function findNode(root: THREE.Object3D, name: string): THREE.Object3D | null {
  const stack: THREE.Object3D[] = [root];
  while (stack.length > 0) {
    const node = stack.pop() as THREE.Object3D;
    if (node.name === name) return node;
    for (const child of node.children) stack.push(child);
  }
  return null;
}

/** Mesh-bearing node names under a subtree, sorted. */
function meshNames(root: THREE.Object3D): string[] {
  const names: string[] = [];
  root.traverse((child: THREE.Object3D) => {
    if ((child as { isMesh?: boolean }).isMesh && child.name) names.push(child.name);
  });
  return names.sort();
}

/**
 * Measure the radial band an object's vertices occupy, about a vertical axis.
 *
 * Distance from the axis is invariant under the board's rotation, so this reads
 * the same at any board angle - which is what makes it usable as an alignment
 * assertion in a test that has already turned the board.
 */
function toRadialExtent(
  object: THREE.Object3D,
  axisX: number,
  axisY: number,
): RadialExtentSnapshot {
  object.updateWorldMatrix(true, true);

  const point = new THREE.Vector3();
  let min = Infinity;
  let max = -Infinity;

  object.traverse((child: THREE.Object3D) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const position = mesh.geometry.getAttribute('position');
    if (!position) return;

    for (let i = 0; i < position.count; i += 1) {
      point.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
      const radius = Math.hypot(point.x - axisX, point.y - axisY);
      if (radius < min) min = radius;
      if (radius > max) max = radius;
    }
  });

  if (min === Infinity) return { min: 0, max: 0 };
  return { min, max };
}

/**
 * The DOM roots the app builds its overlays into.
 *
 * Passed as one object rather than as four positional arguments: three of them
 * are `HTMLElement`s and one is a `HTMLCanvasElement`, so a caller that got the
 * order wrong would still typecheck.
 */
export interface AppRoots {
  canvas: HTMLCanvasElement;
  hud: HTMLElement;
  display: HTMLElement;
  debugView: HTMLElement;
}

/**
 * Wires the game together and owns the per-step / per-frame ordering.
 *
 * ---------------------------------------------------------------------------
 * THE ROUND
 * ---------------------------------------------------------------------------
 *   beginRound()  the board returns to rotation zero, the ball is spawned HELD
 *                 above the top of the maze, and the 3-2-1 countdown starts.
 *   countdown     the board still responds to input. This is the AIMING WINDOW:
 *                 the descent the ball will take is chosen here, before it is
 *                 released.
 *   release       gravity is handed back and the ball falls into the outermost
 *                 channel at the top of the board.
 *   descent       the ball works its way through the maze. The board is still
 *                 steerable - a radial wall sweeps with it and can lift the ball
 *                 off a wall it has settled against.
 *   exit          the ball leaves through the gap in the guard at the bottom,
 *                 and where it lands along the row depends on the exit angle.
 *   pocket        the ball comes to rest in one of seven fixed pockets. The
 *                 pocket it ends in is the score.
 *
 * ---------------------------------------------------------------------------
 * ORDERING INSIDE A FIXED STEP
 * ---------------------------------------------------------------------------
 *   1. read input
 *   2. advance the countdown / round clock
 *   3. rotate the board's kinematic target
 *   4. step the simulation
 *   5. copy simulated transforms onto the meshes
 *   6. resolve scoring from the resulting ball state
 *
 * The kinematic target must be set before `step()` and exactly once per step -
 * Rapier derives the board's velocity from the delta it is handed.
 *
 * Construction is split into three phases because the visuals are loaded models:
 * `new App()` builds everything that does not depend on assets, `load()` fetches
 * them, and `start()` begins simulating. Nothing simulates before `start()`, so
 * a slow or failed load cannot produce a half-built machine.
 */
export class App {
  private readonly sceneManager: SceneManager;
  private readonly cameraRig: CameraRig;
  private readonly physics: PhysicsWorld;
  private readonly hud: Hud;
  private readonly display: Display;
  private readonly debugView: DebugView;
  private readonly loop: GameLoop;
  private readonly input: InputState;
  private readonly controls: DebugControls;
  private readonly router: InputRouter;
  private readonly gyro: GyroController;
  private readonly mobileControls: MobileControls;

  // ---- arcade loop (P1) ---------------------------------------------------
  /** Balls left this run; a miss costs one, running out ends the game. */
  private lives = GAME.balls;
  /** Best total across sessions, persisted to localStorage. */
  private best = loadBest();
  private readonly overlay: FlowOverlay;
  private readonly assets = new GameAssets(GAME_MODEL_PATHS);
  private readonly layout: MazeLayout = buildMazeLayout();
  private readonly scoring = new ScoringTracker();

  /** Assigned by `load()`. Nothing touches these before then. */
  private maze!: MazeAssembly;
  private ball!: Ball;
  private scoreRow!: ScoreRowRig;
  private cabinet: THREE.Object3D | null = null;

  /**
   * Where the marquee display sits, in world space.
   *
   * Defaults to the modelled LED panel's position so the display is in the right
   * place even if the cabinet fails to load; `load()` overwrites it with the
   * measured value.
   */
  private readonly displayAnchor = new THREE.Vector3(0, 1.52, 0.151);
  private readonly displayHalf = new THREE.Vector2(0.43, 0.095);

  private state: GameState = GameState.Loading;
  /** Smoothed board angular velocity, rad/s. */
  private omega = 0;
  private frames = 0;
  private pixelSamplePending = false;
  private lastPixelSample: PixelSample | null = null;

  /** Points awarded this round. Zero while the round is live. */
  private roundPoints = 0;
  /** Points across every round played since boot. */
  private totalScore = 0;
  /** How many times this round has awarded points. Must never exceed 1. */
  private scoreEvents = 0;
  private round = 1;
  private outcome: string = 'none';

  /**
   * Simulated seconds left on the countdown.
   *
   * Counted down in FIXED STEPS rather than against the wall clock, so the
   * countdown is the same length on a 144 Hz display and on a software-rendered
   * headless run that manages four frames a second.
   */
  private countdownRemaining = 0;
  /** Simulated seconds since the ball was released. */
  private roundElapsed = 0;

  constructor(roots: AppRoots) {
    this.sceneManager = new SceneManager(roots.canvas);
    this.cameraRig = new CameraRig();
    buildLighting(this.sceneManager.scene);

    this.physics = new PhysicsWorld();

    this.hud = new Hud(roots.hud);
    this.display = new Display(roots.display);
    this.debugView = new DebugView(roots.debugView);
    this.input = createInputState();
    this.router = new InputRouter(this.input);
    this.controls = new DebugControls(roots.canvas, this.input, this.router);
    this.gyro = new GyroController(this.router);
    this.mobileControls = new MobileControls(roots.hud, this.router, this.input, this.gyro);
    this.overlay = new FlowOverlay(roots.hud, () => this.beginFromUi());
    this.loop = new GameLoop(this.fixedStep, this.renderFrame);

    this.resize();
    window.addEventListener('resize', this.resize);
  }

  /**
   * Fetch the models the game renders and build the machine.
   *
   * A failed load is recorded and reported; the game recovers with procedural
   * geometry and says so. It never pretends the asset arrived.
   */
  async load(): Promise<void> {
    const [board, ball, cabinet, row] = await Promise.all([
      this.assets.load('maze_assembly'),
      this.assets.load('ball'),
      this.assets.load('cabinet'),
      this.assets.load('scoring_row'),
    ]);

    const fallbacks: [string, unknown, string][] = [
      ['maze_assembly', board, 'maze_assembly.glb did not load; using procedural board'],
      ['ball', ball, 'ball.glb did not load; using procedural sphere'],
      ['cabinet', cabinet, 'cabinet.glb did not load; the machine shell is missing'],
      ['scoring_row', row, 'scoring_row.glb did not load; using procedural pockets'],
    ];
    for (const [name, model, reason] of fallbacks) {
      if (!model) this.assets.markFallback(name, reason);
    }

    this.maze = new MazeAssembly(this.physics.world, this.layout, board?.object);
    this.sceneManager.scene.add(this.maze.group);

    // The cabinet and the scoring row are bolted to the WORLD, not to the board:
    // they must not turn when the board turns, or the pockets would slide out
    // from under the ball while it was falling.
    if (cabinet) {
      this.cabinet = cabinet.object;
      this.cabinet.name = 'cabinet';
      this.sceneManager.scene.add(this.cabinet);
      this.measureDisplayAnchor(this.cabinet);
    }

    this.scoreRow = new ScoreRowRig(this.physics.world, row?.object);
    this.sceneManager.scene.add(this.scoreRow.group);

    this.ball = new Ball(this.physics.world, this.sceneManager.scene, ball?.object);
  }

  /**
   * Read the marquee's position off the cabinet itself.
   *
   * The display is placed by projecting the modelled `cabinet_led_panel`, so it
   * lands on the bezel at any viewport size and moves with the marquee if the
   * cabinet is rebuilt. Falls back to the compiled-in default when the cabinet is
   * missing or the node has been renamed, rather than leaving the display
   * unplaced.
   */
  private measureDisplayAnchor(cabinet: THREE.Object3D): void {
    const node = findNode(cabinet, 'cabinet_led_panel');
    if (!node) return;

    const box = new THREE.Box3().setFromObject(node);
    if (box.isEmpty()) return;

    box.getCenter(this.displayAnchor);
    this.displayHalf.set((box.max.x - box.min.x) / 2, (box.max.y - box.min.y) / 2);
  }

  start(): void {
    this.enterReady();
    this.exposeDebugApi();
    this.loop.start();
  }

  /** Open the title screen: the machine is set up but frozen until the player starts. */
  private enterReady(): void {
    this.beginRound(false);
    this.state = GameState.Ready;
  }

  /** The overlay's 開始 / 再玩一次 button — start a run, or a fresh run from GameOver. */
  private beginFromUi(): void {
    if (this.state === GameState.GameOver) this.resetAll();
    else this.beginRound(false);
  }

  dispose(): void {
    this.loop.stop();
    this.controls.dispose();
    this.mobileControls.dispose();
    this.gyro.dispose();
    window.removeEventListener('resize', this.resize);
    this.sceneManager.dispose();
  }

  private readonly resize = (): void => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.sceneManager.resize(width, height);
    this.cameraRig.resize(width, height);
  };

  private readonly fixedStep = (dt: number): void => {
    this.controls.update();
    this.router.apply();
    this.updateOmega(dt);

    if (this.input.resetRequested) {
      this.input.resetRequested = false;
      // ↻ / the key mean different things per state: start the game from the
      // title screen, start a fresh run from GameOver, and re-roll the same run
      // otherwise (countdown, playing, or a resolved screen).
      if (this.state === GameState.Ready) this.beginRound(false);
      else if (this.state === GameState.GameOver) this.resetAll();
      else this.restartRound();
    }

    this.advanceClock(dt);

    this.maze.step(this.omega, dt);
    this.physics.step();

    this.maze.syncVisuals();
    this.ball.syncVisuals();

    if (this.state === GameState.Playing) {
      const position = this.ball.position;
      const resolved = this.scoring.update(position.x, position.y, this.ball.speed);
      if (resolved) {
        this.resolveRound(resolved);
      } else if (this.roundElapsed >= ROUND.timeoutSeconds) {
        // The backstop. Under pure gravity with velocity-proportional damping the
        // ball always keeps descending, so a round that has not scored has almost
        // always simply not finished - but a ball wedged in a corner must not hang
        // the game forever.
        this.resolveRound(this.scoring.timeout(position.x, position.y));
      }
    }
  };

  /**
   * Advance the countdown and the round clock.
   *
   * Both are counted in SIMULATED seconds, which is what makes the aiming window
   * the same length regardless of how fast the machine renders.
   */
  private advanceClock(dt: number): void {
    // Hold the ball during the aiming windows — the Ready title screen AND the
    // countdown. The ball is under gravity in both, so without this it would roll
    // into the maze before the player starts and while the countdown is still
    // choosing a descent. Only the countdown actually counts down.
    if (this.state === GameState.Ready) {
      this.ball.hold();
      return;
    }

    if (this.state === GameState.Countdown) {
      this.countdownRemaining = Math.max(0, this.countdownRemaining - dt);
      // Held every step, not once: the board's colliders are moving underneath,
      // so a ball merely given zero gravity could still be picked up and carried.
      this.ball.hold();
      if (this.countdownRemaining <= 0) this.releaseBall();
      return;
    }

    if (this.state === GameState.Playing) this.roundElapsed += dt;
  }

  /** Hand the ball back to gravity and start the round clock. */
  private releaseBall(): void {
    this.countdownRemaining = 0;
    this.roundElapsed = 0;
    this.ball.release();
    this.state = GameState.Playing;
  }

  /**
   * Ease `omega` toward whatever the input layer is asking for.
   *
   * Accelerating and decelerating at different rates is what makes the board
   * feel like a weighted steering wheel rather than a switch.
   */
  private updateOmega(dt: number): void {
    const requested = this.input.forcedOmega ?? this.input.omegaTarget;
    const target = clamp(requested, -ROTATION.maxOmega, ROTATION.maxOmega);
    const rate = target === 0 ? ROTATION.decay : ROTATION.accel;
    this.omega = moveTowards(this.omega, target, rate * dt);
  }

  /**
   * Turn a one-shot scoring result into a game-state transition.
   *
   * Reached at most once per round: `ScoringTracker.update()` returns non-null
   * only on the step that resolved the round, so the ball cannot be paid twice
   * for one descent.
   */
  private resolveRound(result: ScoreResult): void {
    this.scoreEvents += 1;
    this.roundPoints = result.points;
    this.totalScore += result.points;
    this.outcome = result.outcome;
    this.state = result.outcome === 'lost' ? GameState.BallLost : GameState.Scored;

    // A miss costs a ball; running out ends the run. A score never costs one.
    if (result.outcome === 'lost') {
      this.lives -= 1;
      if (this.lives <= 0) {
        this.state = GameState.GameOver;
        if (this.totalScore > this.best) {
          this.best = this.totalScore;
          saveBest(this.best);
        }
      }
    }
  }

  private restartRound(): void {
    this.beginRound(true);
  }

  /**
   * Start a round.
   *
   * Deterministic and complete: the board returns to rotation zero, the ball is
   * respawned held above the entry point, the countdown restarts, and the round's
   * points go back to zero. The running total deliberately survives a restart -
   * only `resetAll()` clears it.
   */
  private beginRound(advanceRound: boolean): void {
    this.scoring.reset();
    this.roundPoints = 0;
    this.scoreEvents = 0;
    this.outcome = 'none';
    if (advanceRound) this.round += 1;

    this.omega = 0;
    this.input.forcedOmega = null;
    // Back to a known orientation, so a round does not inherit the previous
    // round's board angle and with it a different set of available descents.
    this.maze.resetAngle();
    this.ball.reset();

    this.countdownRemaining = COUNTDOWN.seconds;
    this.roundElapsed = 0;
    this.state = GameState.Countdown;
  }

  /** Restart and also zero the running total. */
  private resetAll(): void {
    this.totalScore = 0;
    this.lives = GAME.balls;
    this.beginRound(false);
    // A full reset starts the round counter over rather than continuing it.
    this.round = 1;
  }

  /** Which production models loaded, and whether any fell back. */
  assetReport(): AssetReport {
    return this.assets.report();
  }

  /** Surface an asset-load failure to the player. */
  showAssetFailure(messages: readonly string[]): void {
    this.hud.showAssetFailure(messages);
  }

  private readonly renderFrame = (): void => {
    this.frames += 1;

    this.sceneManager.render(this.cameraRig.camera);

    // Must run in the same frame as render(): the drawing buffer is only
    // guaranteed readable until the frame ends.
    if (this.pixelSamplePending) {
      this.pixelSamplePending = false;
      this.lastPixelSample = this.sceneManager.readPixelSample();
    }

    this.display.place(
      this.cameraRig.camera,
      window.innerWidth,
      window.innerHeight,
      this.displayAnchor,
      this.displayHalf.x,
      this.displayHalf.y,
    );
    this.display.update({
      state: this.state,
      countdown: this.state === GameState.Countdown ? this.countdownRemaining : null,
      elapsed: this.roundElapsed,
      score: this.roundPoints,
      totalScore: this.totalScore,
      slotLabel: this.scoring.result?.slotLabel ?? null,
      round: this.round,
    });

    this.hud.update({
      angleDeg: this.maze.angleDeg,
      ballSpeed: this.ball.speed,
      state: stateLabel(this.state),
      score: this.roundPoints,
      totalScore: this.totalScore,
      slotLabel: this.scoring.result?.slotLabel ?? null,
      round: this.round,
      held: this.ball.isHeld,
    });

    this.overlay.sync(this.state, { total: this.totalScore, best: this.best });

    // The debug view draws from the COLLISION data, not the rendered scene, so it
    // is redrawn from the live layout rather than from the camera. It does nothing
    // at all while it is hidden.
    if (this.debugView.visible) {
      const position = this.ball.position;
      this.debugView.draw({
        layout: this.layout,
        boardAngleRad: this.maze.angleRadians,
        ball: { x: position.x, y: position.y },
        ballColliderRadius: BALL.radius + BALL.contactSkin,
      });
    }
  };

  private exposeDebugApi(): void {
    installDebugApi({
      snapshot: () => {
        const position = this.ball.position;
        const result = this.scoring.result;
        return {
          state: this.state,
          ballX: position.x,
          ballY: position.y,
          ballZ: position.z,
          ballSpeed: this.ball.speed,
          ballRadius: this.ball.radiusFromCenter,
          angleDeg: this.maze.angleDeg,
          omega: this.omega,
          spawned: this.ball.isSpawned,
          ballSleeping: this.ball.isSleeping,
          bodyCount: this.physics.bodyCount,
          colliderCount: this.physics.colliderCount,
          wallSegments: this.layout.segments.length,
          frames: this.frames,

          round: this.round,
          score: this.roundPoints,
          totalScore: this.totalScore,
          slotId: result?.slotId ?? null,
          slotLabel: result?.slotLabel ?? null,
          outcome: this.outcome,
          resolved: this.scoring.isResolved,
          restSteps: this.scoring.consecutiveRestSteps,
          scoreEvents: this.scoreEvents,

          countdownRemaining: this.countdownRemaining,
          held: this.ball.isHeld,
          roundElapsed: this.roundElapsed,
          hasEnteredRow: this.scoring.hasEnteredRow,

          scoreRowColliders: this.scoreRow.colliders.length,
          layoutObstacles: this.layout.obstacles.length,
          scoreRow: {
            count: SCORE_ROW.count,
            mouthY: SCORE_ROW.mouthY,
            floorY: SCORE_ROW.floorY,
            span: SCORE_ROW.span,
            totalWidth: SCORE_ROW.totalWidth,
          },
          cabinetVisible: this.cabinet !== null,
          ballsLeft: this.lives,
          best: this.best,
        };
      },
      setOmega: (omega: number): void => {
        this.input.forcedOmega = omega;
      },
      setBoardAngle: (deg: number): void => {
        this.maze.setAngle(degToRad(deg));
      },
      reset: (): void => {
        this.restartRound();
      },
      resetAll: (): void => {
        this.resetAll();
      },
      spawn: (): void => {
        this.ball.spawn();
      },
      releaseBall: (): void => {
        this.releaseBall();
      },
      requestPixelSample: (): void => {
        this.pixelSamplePending = true;
      },
      pixelSample: (): PixelSample | null => this.lastPixelSample,

      assetReport: (): AssetReport => this.assets.report(),
      boardNodeNames: (): string[] => meshNames(this.maze.board),
      cabinetNodeNames: (): string[] => (this.cabinet ? meshNames(this.cabinet) : []),
      scoreRowNodeNames: (): string[] => meshNames(this.scoreRow.visual),
      displayText: () => ({
        timer: this.display.timerText,
        round: this.display.roundText,
        total: this.display.totalText,
      }),
      controlState: () => ({
        coarsePointer: window.matchMedia?.('(pointer: coarse)').matches ?? false,
        gyroSupported: this.gyro.isSupported,
        gyroEnabled: this.gyro.isEnabled,
        gyroNeutralDeg: this.gyro.neutralDeg,
      }),
      boardBounds: (): BoundsSnapshot => toBounds(new THREE.Box3().setFromObject(this.maze.board)),
      cabinetBounds: (): BoundsSnapshot | null =>
        this.cabinet ? toBounds(new THREE.Box3().setFromObject(this.cabinet)) : null,
      nodeBounds: (name: string): BoundsSnapshot | null => {
        const found = findNode(this.maze.board, name) ?? (this.cabinet ? findNode(this.cabinet, name) : null);
        if (!found) return null;
        return toBounds(new THREE.Box3().setFromObject(found));
      },
      nodeRadialExtent: (name: string): RadialExtentSnapshot | null => {
        const found = findNode(this.maze.board, name);
        if (!found) return null;
        const axis = this.maze.group.getWorldPosition(new THREE.Vector3());
        return toRadialExtent(found, axis.x, axis.y);
      },
      teleportBall: (x: number, y: number, z: number): void => {
        this.ball.teleport(x, y, z);
      },
      /**
       * The scoring rule itself, exposed as a pure function.
       *
       * A test can ask "which pocket is x in?" without driving a ball anywhere,
       * which is what makes the pocket boundaries directly assertable rather than
       * only inferable from where balls happen to land.
       */
      slotAt: (x: number) => {
        const slot = resolveSlot(x);
        return slot
          ? { id: slot.id, label: slot.label, points: slot.points, tier: slot.tier }
          : null;
      },
      setDebugView: (visible: boolean): void => {
        this.debugView.setVisible(visible);
      },
      debugViewStats: () => this.debugView.stats,
      projectPoint: (x: number, y: number, z: number) => {
        const camera = this.cameraRig.camera;
        const canvas = this.sceneManager.canvas;
        const width = canvas.clientWidth || canvas.width;
        const height = canvas.clientHeight || canvas.height;
        const point = new THREE.Vector3(x, y, z).project(camera);
        // `project` puts anything behind the camera at a mirrored NDC position
        // rather than reporting it, so `z > 1` is the only honest "not visible".
        return {
          x: (point.x * 0.5 + 0.5) * width,
          y: (-point.y * 0.5 + 0.5) * height,
          visible: point.z <= 1,
        };
      },
      slotAnchors: () => {
        const camera = this.cameraRig.camera;
        const canvas = this.sceneManager.canvas;
        const width = canvas.clientWidth || canvas.width;
        const height = canvas.clientHeight || canvas.height;
        return slotsLeftToRight().map((slot) => {
          // The BACK PANEL's centre, not the mouth: the mouth is a plane in
          // mid-air and sampling there would return whatever is behind the row.
          const x = slot.centerX;
          const y = SCORE_ROW.midY;
          const z = SCORE_ROW.backZ;
          const point = new THREE.Vector3(x, y, z).project(camera);
          return {
            id: slot.id,
            tier: slot.tier,
            points: slot.points,
            x,
            y,
            z,
            screenX: (point.x * 0.5 + 0.5) * width,
            screenY: (-point.y * 0.5 + 0.5) * height,
            visible: point.z <= 1,
          };
        });
      },
    });
  }
}
